import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { clampAndWrapIdentity, expandIdentityString } from "../identityFilterUtils.ts";
import {
  FloatingPopover,
  GridIcon,
  IdChip,
  SearchIcon,
  SuggestRow,
  XIcon,
  getInitialIdText,
  getNoun,
  sharedStyles as s,
  useGroupData,
  useIdentityEmit,
  useItemById,
  type VariantProps,
} from "./shared.tsx";

type Intent<T> =
  | { kind: "empty" }
  | { kind: "id"; expanded: number[]; text: string }
  | { kind: "search"; items: T[] }
  | { kind: "group"; fieldLabel: string; matches: Array<{ value: string; ids: number[] }> };

const MAX_VISIBLE_CHIPS = 4;

/**
 * Variation D — "Command palette".
 * Chips live inside one input container (combobox pattern). Typed intent is
 * auto-detected: digits → ID range, text → search, `#field:value` → bulk-add.
 * Overflow (+N more) opens a popover with full list + "ID range" view.
 */
export function PaletteVariant<T>({
  config,
  columnName,
  isParameter,
  value,
  onChange,
  onSearch,
}: VariantProps<T>) {
  const [idText, setIdText] = useState<string>(() => getInitialIdText(value));
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [morePop, setMorePop] = useState(false);
  const [asIdList, setAsIdList] = useState(false);

  const comboAnchorRef = useRef<HTMLDivElement>(null);
  const moreAnchorRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const emit = useIdentityEmit(columnName, isParameter, onChange);
  const groupData = useGroupData(config);
  const byId = useItemById(config);
  const noun = getNoun(config);

  useEffect(() => {
    if (value.length === 0) {
      setIdText("");
    } else if (value[0]?.oValue != null) {
      setIdText(String(value[0].oValue));
    }
  }, [value]);

  const ids = useMemo(() => expandIdentityString(idText), [idText]);

  const update = useCallback(
    (text: string) => {
      setIdText(text);
      emit(text);
    },
    [emit],
  );

  const removeId = useCallback(
    (id: number) => update(clampAndWrapIdentity(ids.filter((x) => x !== id))),
    [ids, update],
  );

  const intent = useMemo<Intent<T>>(() => {
    const t = q.trim();
    if (!t) return { kind: "empty" };
    if (/^[0-9,\-\s]+$/.test(t)) {
      const stripped = t.replace(/\s/g, "");
      return { kind: "id", expanded: expandIdentityString(stripped), text: stripped };
    }
    // `#field:value` — field matches any group label or its non-space prefix
    // (e.g. "#city:", "#retail host:", "#store:"). Multi-word labels and
    // labels with punctuation are supported since we match up to the first
    // `:` rather than relying on [a-z]+.
    const hash = t.match(/^#\s*([^:]+?)\s*:\s*(.+)$/);
    if (hash && config.groupFields) {
      const prefix = hash[1]!.toLowerCase();
      const gf =
        config.groupFields.find((g) => g.label.toLowerCase() === prefix) ??
        config.groupFields.find((g) => g.label.toLowerCase().startsWith(prefix)) ??
        config.groupFields.find((g) => g.label.toLowerCase().includes(prefix));
      if (gf && groupData) {
        const vm = groupData.get(gf.field);
        if (vm) {
          const lc = hash[2]!.toLowerCase();
          const matches: Array<{ value: string; ids: number[] }> = [];
          for (const [v, arr] of vm) if (v.toLowerCase().includes(lc)) matches.push({ value: v, ids: arr });
          return { kind: "group", fieldLabel: gf.label, matches };
        }
      }
    }
    const lc = t.toLowerCase();
    const items: T[] = [];
    for (const it of config.items) {
      if (config.matchFn(it, lc)) {
        items.push(it);
        if (items.length >= 30) break;
      }
    }
    return { kind: "search", items };
  }, [q, config, groupData]);

  const commit = useCallback(() => {
    if (intent.kind === "id") {
      update(clampAndWrapIdentity([...ids, ...intent.expanded]));
      setQ("");
    } else if (intent.kind === "search" && intent.items.length) {
      // Add ALL matches — matches CompactVariant + the "X matches · Press ↵ to
      // add" hint. Single-pick is via row click. Don't fire parent search.
      update(clampAndWrapIdentity([...ids, ...intent.items.map(config.getIdFromItem)]));
      setQ("");
    } else if (intent.kind === "group" && intent.matches[0]) {
      update(clampAndWrapIdentity([...ids, ...intent.matches[0]!.ids]));
      setQ("");
    } else if (intent.kind === "empty" && ids.length) {
      // Bare Enter with existing chips → trigger search
      onSearch();
    }
  }, [intent, ids, config, update, onSearch]);

  /* FloatingPopover handles outside-click for both pops */

  const visible = ids.slice(0, MAX_VISIBLE_CHIPS);
  const hidden = ids.length - visible.length;

  return (
    <div style={{ position: "relative" }}>
      <div
        ref={comboAnchorRef}
        onClick={() => inputRef.current?.focus()}
        style={{
          display: "flex", flexWrap: "wrap", alignItems: "center", gap: "var(--space-0-5)",
          border: "1px solid var(--color-border-default)",
          borderRadius: "var(--radius-1)",
          background: "var(--color-bg)",
          padding: "3px var(--space-0-5)",
          minHeight: 28, cursor: "text",
          maxWidth: 520,
        }}
      >
        <span style={{ padding: "0 var(--space-0-5) 0 var(--space-1)", color: "var(--color-text-muted)", display: "inline-flex", alignItems: "center", height: 22 }}>
          <SearchIcon size={13} />
        </span>

        {visible.map((id) => (
          <IdChip
            key={id}
            id={id}
            item={byId.get(id)}
            config={config}
            onRemove={() => removeId(id)}
          />
        ))}

        {hidden > 0 && (
          <div ref={moreAnchorRef} style={{ position: "relative", display: "inline-flex" }}>
            <button
              type="button"
              className={s.chipDim}
              style={{ fontWeight: 600, border: "none", cursor: "pointer", padding: "2px var(--space-2)" }}
              onClick={(e) => { e.stopPropagation(); setMorePop((o) => !o); }}
            >
              +{hidden} more
            </button>
            <FloatingPopover
              open={morePop}
              anchorRef={moreAnchorRef}
              onClose={() => setMorePop(false)}
              width={340}
              offsetY={6}
              className={s.popover}
            >
              <div onClick={(e) => e.stopPropagation()}>
                <div style={{ display: "flex", alignItems: "center", padding: "var(--space-1) var(--space-2)", borderBottom: "1px solid var(--color-bg-muted)", gap: "var(--space-2)" }}>
                  <span style={{ fontSize: "var(--text-xs)", fontWeight: 600 }}>
                    {ids.length} {noun}{ids.length === 1 ? "" : "s"} selected
                  </span>
                  <div className={s.spacer} />
                  <div className={s.seg}>
                    <button type="button" className={!asIdList ? s.segBtnActive : s.segBtn} onClick={() => setAsIdList(false)}>List</button>
                    <button type="button" className={asIdList ? s.segBtnActive : s.segBtn} onClick={() => setAsIdList(true)}>ID range</button>
                  </div>
                </div>
                {!asIdList ? (
                  <div style={{ maxHeight: 280, overflow: "auto" }}>
                    {ids.map((id) => {
                      const it = byId.get(id);
                      return (
                        <div key={id} className={s.suggestItem}>
                          <span className={s.suggestId}>#{id}</span>
                          <span className={s.suggestName}>{it ? String((it as Record<string, unknown>)[config.nameField] ?? "") : "—"}</span>
                          <button type="button" className={s.btnGhost} onClick={() => removeId(id)} aria-label={`Remove ${id}`}>
                            <XIcon size={10} />
                          </button>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div style={{ padding: "var(--space-2)" }}>
                    <textarea
                      readOnly
                      value={idText}
                      onFocus={(e) => e.currentTarget.select()}
                      style={{
                        width: "100%", minHeight: 80, resize: "vertical",
                        border: "1px solid var(--color-border-default)",
                        borderRadius: "var(--radius-1)", padding: "var(--space-1)",
                        fontFamily: "ui-monospace, monospace", fontSize: "var(--text-xs)",
                        fontVariantNumeric: "tabular-nums",
                        background: "var(--color-bg-muted)",
                        color: "var(--color-text)",
                      }}
                    />
                    <div className={s.subtleHint} style={{ marginTop: "var(--space-1)" }}>
                      Click to select all · paste anywhere
                    </div>
                  </div>
                )}
                <div className={s.popFooter}>
                  <button type="button" className={s.btnGhost} onClick={() => { update(""); setMorePop(false); }}>
                    Clear all
                  </button>
                  <div className={s.spacer} />
                  <button type="button" className={s.btn} onClick={() => setMorePop(false)}>Done</button>
                </div>
              </div>
            </FloatingPopover>
          </div>
        )}

        <input
          ref={inputRef}
          value={q}
          onChange={(e) => { setQ(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          onKeyDown={(e) => {
            if (e.key === "Enter") { e.preventDefault(); commit(); }
            else if (e.key === "Escape") setOpen(false);
            else if (e.key === "Backspace" && !q && ids.length) { e.preventDefault(); removeId(ids[ids.length - 1]!); }
          }}
          placeholder={ids.length ? "" : `Search, IDs, or #${config.groupFields?.[0]?.label.toLowerCase() ?? "field"}:value`}
          style={{
            flex: 1, minWidth: 120, border: "none", outline: "none",
            padding: "0 var(--space-1)", height: 22, font: "inherit",
            fontSize: "var(--text-sm)", background: "transparent",
            color: "var(--color-text)",
          }}
        />
      </div>

      <FloatingPopover
        open={open && !!q}
        anchorRef={comboAnchorRef}
        onClose={() => setOpen(false)}
        width={480}
        className={s.popover}
      >
        <div>
          <div style={{
            padding: "var(--space-1) var(--space-2)", fontSize: "var(--text-xs)",
            color: "var(--color-text-muted)",
            borderBottom: "1px solid var(--color-bg-muted)",
            background: "var(--color-bg-muted)",
            display: "flex", justifyContent: "space-between",
          }}>
            <span>
              {intent.kind === "id" && `ID range → ${intent.expanded.length} ${noun}${intent.expanded.length === 1 ? "" : "s"}`}
              {intent.kind === "search" && `${intent.items.length} match${intent.items.length === 1 ? "" : "es"}`}
              {intent.kind === "group" && `Group by ${intent.fieldLabel} → ${intent.matches.length} value${intent.matches.length === 1 ? "" : "s"}`}
            </span>
            <span>Press <span className={s.kbd}>↵</span> to add</span>
          </div>
          <div style={{ maxHeight: 260, overflow: "auto" }}>
            {intent.kind === "id" && (
              <div style={{ padding: "var(--space-3)", fontSize: "var(--text-xs)", color: "var(--color-text)" }}>
                Will add IDs <code style={{ background: "var(--color-bg-muted)", padding: "2px 6px", borderRadius: "var(--radius-1)", fontVariantNumeric: "tabular-nums" }}>{intent.text}</code> ({intent.expanded.length} entries)
              </div>
            )}
            {intent.kind === "search" && intent.items.map((it, i) => {
              const id = config.getIdFromItem(it);
              return (
                <SuggestRow
                  key={i}
                  item={it}
                  config={config}
                  selected={ids.includes(id)}
                  showCheck
                  onClick={() => {
                    if (ids.includes(id)) removeId(id);
                    else update(clampAndWrapIdentity([...ids, id]));
                    setQ("");
                  }}
                />
              );
            })}
            {intent.kind === "group" && intent.matches.map((m, i) => (
              <div
                key={i}
                className={s.suggestItem}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => {
                  update(clampAndWrapIdentity([...ids, ...m.ids]));
                  setQ("");
                }}
              >
                <GridIcon size={12} />
                <span className={s.suggestName}>{m.value}</span>
                <span className={s.suggestMeta}>{m.ids.length} {noun}s</span>
              </div>
            ))}
          </div>
          {config.groupFields && config.groupFields.length > 0 && intent.kind === "search" && (
            <div style={{ padding: "var(--space-1) var(--space-2)", borderTop: "1px solid var(--color-bg-muted)", fontSize: "var(--text-xs)", color: "var(--color-text-muted)", display: "flex", flexWrap: "wrap", gap: "var(--space-1)", alignItems: "center" }}>
              <span>Bulk-add by field:</span>
              {config.groupFields.slice(0, 6).map((gf) => (
                <code
                  key={gf.field}
                  style={{ background: "var(--color-bg-muted)", padding: "1px 5px", borderRadius: "var(--radius-1)", cursor: "pointer" }}
                  onMouseDown={(e) => { e.preventDefault(); setQ(`#${gf.label.toLowerCase()}:`); inputRef.current?.focus(); }}
                >
                  #{gf.label.toLowerCase()}:
                </code>
              ))}
              {config.groupFields.length > 6 && <span>+{config.groupFields.length - 6} more</span>}
            </div>
          )}
        </div>
      </FloatingPopover>
    </div>
  );
}
